import { Box, Container, Divider, Typography, useTheme } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import useResponsive from '../../hooks/useResponsive';
import useAuth from '../../hooks/useAuth';
import { Result } from '../Exams/type';
import { fetchResultByStudentId } from '../../api';
import {
  CalculateScore,
  formatCompleteTime,
  getRank,
} from '../Exams/extractQuestions';
import { translateSubject } from '../Exams/docUtils';
import LoadingDisplay from './Loading';
import ErrorDisplay from './Error';
import NotFoundPage from '../Error/NotFoundPage';

// HistoryPage props
interface HistoryPageProps {
  filter?: string;
}

const HistoryPage: FC<HistoryPageProps> = ({ filter }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const [sortedResults, setSortedResults] = useState<Result[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const StyledTypography = ({ children, ...props }: any) => (
    <Typography
      textAlign="center"
      fontWeight={600}
      {...props}
      sx={{
        background: `linear-gradient(to right, ${theme.palette.prize.first}, #ff7e5f)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        ...props.sx,
      }}
    >
      {children}
    </Typography>
  );

  useEffect(() => {
    // Filter results by filter props (all, Math, Physic)
    if (filter && sortedResults) {
      setSortedResults(
        sortedResults?.filter(
          (result) => result.exam?.subject === filter || filter === 'all'
        )
      );
    }
  }, [filter]);

  const columnProps = [
    {
      name: 'Ngày',
      width: '10%',
      minWidth: 100,
      maxWidth: '10%',
      render: (result: Result) =>
        new Date(result.createdAt || '').toLocaleDateString(),
    },
    {
      name: 'Môn học',
      width: '10%',
      minWidth: 100,
      maxWidth: '10%',
      render: (result: Result) => translateSubject(result.exam?.subject || ''),
    },
    {
      name: 'Tên đề',
      width: '20%',
      minWidth: 100,
      maxWidth: '20%',
      render: (result: Result) => result.exam?.title || '',
    },
    {
      name: 'Thời gian',
      width: '10%',
      minWidth: 100,
      maxWidth: '10%',
      render: (result: Result) => formatCompleteTime(result.completeTime), //`${result.completeTime || 0}s`,
    },
    {
      name: 'Điểm',
      width: '10%',
      minWidth: 100,
      maxWidth: '10%',
      render: (result: Result) => result.score,
    },
    {
      name: 'Thứ hạng',
      width: '10%',
      minWidth: 100,
      maxWidth: '10%',
      render: (result: Result) => result.rank,
    },
  ];

  useEffect(() => {
    const fetchResults = async () => {
      if (!user?._id) return;
      try {
        const res = await fetchResultByStudentId(user._id);
        const sortedResults: Result[] = res;

        if (sortedResults) {
          const resultsWithScores = sortedResults.map((result) => ({
            ...result,
            score: result.exam
              ? CalculateScore(result.answers, result.exam.questions)
              : 0,
            rank: getRank(result),
          }));
          setSortedResults(resultsWithScores);
          setLoading(false);
        }
      } catch (error) {
        console.error(error);
        setError('Đã xảy ra lỗi. Vui lòng thử lại sau.');
      }
    };
    fetchResults();
  }, [user?._id]);

  if (!user) {
    return <NotFoundPage message="Trang chỉ dành cho tài khoản đăng nhập" />;
  }

  if (loading) {
    return <LoadingDisplay loadingMessage="Đang tải chi tiết..." />;
  }

  if (error) {
    return <ErrorDisplay errorMessage="Đã xảy ra lỗi. Vui lòng thử lại sau." />;
  }

  return (
    <Box>
      <Typography variant="h3" textAlign="center">
        Lịch sử làm bài
      </Typography>
      {sortedResults?.length === 0 ? (
        <Typography variant="h6" textAlign="center">
          Chưa có dữ liệu
        </Typography>
      ) : (
        <>
          {/* <StyledTypography
            variant="h3"
            sx={{ mt: 2, mb: 2, fontWeight: 'bold' }}
          >
            {user?.name}
          </StyledTypography> */}
          <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            width="100%"
            maxWidth="100%"
            sx={{
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px', // Set the height of the horizontal scrollbar
              },
            }}
          >
            <Box width="100%">
              {/* Header Columns */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                px={1}
                py={1}
                mt={1}
                sx={
                  {
                    //borderBottom: `1px solid ${theme.palette.text.secondary}`,
                  }
                }
              >
                {columnProps.map((col, index) => (
                  <StyledTypography
                    key={index}
                    textAlign="center"
                    fontWeight={600}
                    sx={{
                      maxWidth: col.maxWidth,
                      width: col.width,
                      minWidth: col.minWidth,
                    }}
                  >
                    {col.name}
                  </StyledTypography>
                ))}
              </Box>
              <Divider
                sx={{
                  backgroundColor: 'text.secondary',
                  opacity: 1,
                  width: '100%',
                  minWidth: 600,
                }}
              />
            </Box>

            {/* Rows */}
            {sortedResults?.map((result, index) => (
              <Box key={index} width="100%">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  p={1.5}
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {columnProps.map((col, index) => (
                    <Typography
                      key={index}
                      textAlign="center"
                      fontWeight={500}
                      sx={{
                        maxWidth: col.maxWidth,
                        width: col.width,
                        minWidth: col.minWidth,
                        display: '-webkit-box', // Required for limiting lines of text
                        WebkitLineClamp: 1, // Limits to 2 lines; you can adjust this number
                        WebkitBoxOrient: 'vertical', // Required for multiline ellipsis
                        overflow: 'hidden', // Hides the overflowing text
                        textOverflow: 'ellipsis', // Adds '...' at the end of the truncated text
                      }}
                    >
                      {col.render(result)}
                    </Typography>
                  ))}
                </Box>
                <Divider
                  sx={{
                    backgroundColor: 'text.secondary',
                    opacity: 0.3,
                    minWidth: 600,
                  }}
                />
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default HistoryPage;
